import { Star } from "lucide-react";
import { Button } from "@/components/ui/button";

const HeroSection = ({ product, onShopClick, onScienceClick }) => {
  return (
    <section 
      className="relative min-h-screen flex items-center pt-20 overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background Effects - pointer-events-none to not block clicks */}
      <div className="absolute inset-0 radial-glow pointer-events-none" />
      <div className="absolute top-1/4 right-0 w-[600px] h-[600px] bg-nukleus-violet/10 rounded-full blur-[120px] pointer-events-none" />
      
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 w-full">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          {/* Left Content */}
          <div className="text-center lg:text-left order-2 lg:order-1">
            {/* Eyebrow */}
            <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-2 mb-6">
              <span className="w-2 h-2 bg-nukleus-violet rounded-full animate-pulse" />
              <span className="text-xs font-medium text-nukleus-lilac uppercase tracking-wider">
                Nootropic Focus Formula
              </span>
            </div>

            {/* Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-semibold text-white leading-[1.1] tracking-tight mb-6">
              Think Deeper.
              <br />
              <span className="text-nukleus-lilac">Perform Better.</span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg text-white/50 max-w-lg mx-auto lg:mx-0 mb-8 leading-relaxed">
              A precision-dosed blend of Alpha-GPC, Natural Caffeine, L-Theanine, and Asian Ginseng. 
              Engineered for sustained focus without the crash.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start mb-8">
              <Button
                onClick={onShopClick}
                className="w-full sm:w-auto bg-nukleus-violet hover:bg-nukleus-violet/90 text-white rounded-full px-8 py-6 text-base font-semibold shadow-[0_0_30px_rgba(124,58,237,0.4)] hover:shadow-[0_0_40px_rgba(124,58,237,0.6)] transition-all btn-primary"
                data-testid="hero-cta-primary"
              >
                Get Nukleus — ${product?.price || "44.99"}
              </Button>
              <button
                onClick={onScienceClick}
                className="w-full sm:w-auto text-white/70 hover:text-white hover:bg-white/5 rounded-full px-6 py-4 text-base font-medium transition-all group flex items-center justify-center"
                data-testid="hero-cta-secondary"
              >
                See the science 
                <span className="ml-2 group-hover:translate-x-1 transition-transform">→</span>
              </button>
            </div>

            {/* Social Proof */}
            <div className="flex items-center gap-3 justify-center lg:justify-start">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <span className="text-white font-medium">4.9</span>
              <span className="text-white/50">·</span>
              <span className="text-white/50">2,400+ reviews</span>
            </div>
          </div>

          {/* Right - Product Image */}
          <div className="relative order-1 lg:order-2 flex justify-center pointer-events-none">
            {/* Orbital Rings */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-[280px] h-[280px] sm:w-[350px] sm:h-[350px] lg:w-[450px] lg:h-[450px] rounded-full border border-nukleus-violet/20 animate-[spin_30s_linear_infinite]" />
              <div className="absolute w-[320px] h-[320px] sm:w-[400px] sm:h-[400px] lg:w-[520px] lg:h-[520px] rounded-full border border-nukleus-violet/10 animate-[spin_40s_linear_infinite_reverse]" />
            </div>
            
            {/* Glow Effect */}
            <div className="absolute w-[250px] h-[250px] sm:w-[300px] sm:h-[300px] lg:w-[400px] lg:h-[400px] bg-nukleus-violet/30 rounded-full blur-[80px]" />
            
            {/* Product Image */}
            <div className="relative z-10 product-float">
              <img
                src="https://customer-assets.emergentagent.com/job_focus-powder/artifacts/5l4mwejz_Hero%20shot.jpg"
                alt="NUKLEUS Focus Powder"
                className="w-[240px] sm:w-[300px] lg:w-[400px] h-auto object-contain drop-shadow-2xl"
                data-testid="hero-product-image"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
